import { useEffect, useMemo, useRef, useState } from "react";
import { ChevronDown, Search, X, Check } from "lucide-react";
import { useResourceList, type ReferenceDef } from "../../lib/admin/resources";
import { Spinner } from "../ui";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type Rec = Record<string, any>;

/**
 * Searchable entity picker. The user searches and selects a record by name;
 * the component emits that record's numeric id. Never exposes raw ids to type.
 */
export default function EntitySelect({
  reference,
  value,
  initialLabel,
  onChange,
  error,
  disabled,
  placeholder = "Search…",
}: {
  reference: ReferenceDef;
  value: number | null;
  initialLabel?: string;
  onChange: (id: number | null) => void;
  error?: boolean;
  disabled?: boolean;
  placeholder?: string;
}) {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [label, setLabel] = useState<string | undefined>(initialLabel);
  const [active, setActive] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Keep the shown label in sync when the record (edit target) changes.
  useEffect(() => setLabel(initialLabel), [initialLabel]);

  // Debounce the search term so we don't fire a request per keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(search), 200);
    return () => clearTimeout(t);
  }, [search]);

  const { data, isLoading } = useResourceList(reference.endpoint, {
    search: debounced || undefined,
    limit: 20,
  });
  const rows: Rec[] = useMemo(() => data?.results ?? [], [data]);

  // Close on outside click / Escape.
  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 10);
  }, [open]);
  useEffect(() => setActive(0), [debounced, open]);

  function pick(row: Rec) {
    onChange(row.id);
    setLabel(String(row[reference.optionLabel] ?? `#${row.id}`));
    setOpen(false);
    setSearch("");
  }
  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    onChange(null);
    setLabel(undefined);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, rows.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (rows[active]) pick(rows[active]);
    }
  }

  const border = error ? "border-critical/60" : "border-white/10";
  const has = value != null && value !== undefined;

  return (
    <div className="relative" ref={rootRef}>
      <button
        type="button"
        disabled={disabled}
        onClick={() => !disabled && setOpen((o) => !o)}
        className={`flex w-full items-center justify-between gap-2 rounded-lg border bg-white/5 px-3 py-2 text-left text-sm outline-none transition focus:border-aurora-cyan/50 disabled:opacity-50 ${border}`}
      >
        <span className={has ? "truncate text-ink" : "truncate text-slate-500"}>
          {has ? label ?? `#${value}` : placeholder}
        </span>
        <span className="flex shrink-0 items-center gap-1 text-slatey">
          {has && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              onClick={clear}
              className="rounded p-0.5 hover:text-critical"
              title="Clear"
            >
              <X size={14} />
            </span>
          )}
          <ChevronDown size={14} />
        </span>
      </button>

      {open && (
        <div className="absolute left-0 right-0 z-[60] mt-1 overflow-hidden rounded-lg border border-white/10 bg-ocean-800 shadow-xl">
          <div className="flex items-center gap-2 border-b border-white/10 px-2.5 py-1.5">
            <Search size={13} className="text-slatey" />
            <input
              ref={inputRef}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={onKeyDown}
              placeholder={placeholder}
              className="w-full bg-transparent text-sm outline-none placeholder:text-slate-600"
            />
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {isLoading ? (
              <div className="px-3 py-3"><Spinner /></div>
            ) : rows.length === 0 ? (
              <div className="px-3 py-3 text-xs text-slatey">No matches.</div>
            ) : (
              rows.map((row, i) => {
                const primary = String(row[reference.optionLabel] ?? `#${row.id}`);
                const sub = reference.optionSublabel ? row[reference.optionSublabel] : null;
                const selected = row.id === value;
                return (
                  <button
                    key={row.id}
                    type="button"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => pick(row)}
                    className={`flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-sm transition ${
                      i === active ? "bg-aurora-cyan/10" : "hover:bg-white/5"
                    }`}
                  >
                    <span className="min-w-0">
                      <span className="block truncate text-ink">{primary}</span>
                      {sub && <span className="block truncate text-[11px] text-slatey">{sub}</span>}
                    </span>
                    {selected && <Check size={14} className="shrink-0 text-aurora-cyan" />}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
